import { useAuthMe } from "@bodhistreams/queries";
import { useCallback, useEffect, useRef, useState } from "react";
import { APP_URL } from "../../../urls";
import "./BodhiChatFloating.css";
import { IconClose, IconSparkles } from "./Icons";
import Panel from "./Panel";

const FAB_SIZE = 60;
// How far a pointer must travel before it counts as a drag instead of a tap.
const DRAG_THRESHOLD = 6;
// Breathing room kept between the fab and the top / left viewport edges.
const EDGE_MARGIN = 12;
const PANEL_WIDTH = 420;
const PANEL_MIN_HEIGHT = 320;
const PANEL_EDGE_MARGIN = 16;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const readViewport = () => ({
	width: typeof window === "undefined" ? 1024 : window.innerWidth,
	height: typeof window === "undefined" ? 768 : window.innerHeight,
});

const BodhiChatFloating = ({
	bottomOffset = 24,
	rightOffset = 24,
	panelGap = 16,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const { data: authUser, isLoading } = useAuthMe();
	const panelRef = useRef(null);
	const fabRef = useRef(null);

	const [viewport, setViewport] = useState(readViewport);
	// Dragged position, in the same right/bottom space as the anchor. Null
	// means "still parked on the anchor the props describe", so a mini player
	// appearing (a taller bottomOffset) keeps lifting the fab with it.
	const [position, setPosition] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const dragRef = useRef(null);
	// A drag ends with a click event on the button; swallow that one click.
	const suppressClickRef = useRef(false);

	const fabRight = position ? position.right : rightOffset;
	const fabBottom = position ? position.bottom : bottomOffset;

	// Movement bounds. Bottom never goes under the anchor, so the fab can not
	// come to rest over the audio player, exactly like the mobile fab.
	const clampToBounds = useCallback(
		(next, view) => ({
			right: clamp(
				next.right,
				EDGE_MARGIN,
				Math.max(EDGE_MARGIN, view.width - FAB_SIZE - EDGE_MARGIN),
			),
			bottom: clamp(
				next.bottom,
				bottomOffset,
				Math.max(bottomOffset, view.height - FAB_SIZE - EDGE_MARGIN),
			),
		}),
		[bottomOffset],
	);

	// After a drag or a viewport change: clamp vertically, magnet horizontally
	// to whichever viewport edge is closer.
	const settle = useCallback(
		(next, view) => {
			const bounded = clampToBounds(next, view);
			const leftEdge = Math.max(
				EDGE_MARGIN,
				view.width - FAB_SIZE - rightOffset,
			);
			const rightEdge = rightOffset;
			const snapped =
				Math.abs(bounded.right - rightEdge) <=
				Math.abs(bounded.right - leftEdge)
					? rightEdge
					: leftEdge;
			return { right: snapped, bottom: bounded.bottom };
		},
		[clampToBounds, rightOffset],
	);

	useEffect(() => {
		const handleResize = () => {
			const view = readViewport();
			setViewport(view);
			setPosition((current) => (current ? settle(current, view) : current));
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [settle]);

	// The anchor moved (mini player appeared, offsets changed): re-settle.
	useEffect(() => {
		setPosition((current) =>
			current ? settle(current, readViewport()) : current,
		);
	}, [settle]);

	const handlePointerDown = (event) => {
		if (event.pointerType === "mouse" && event.button !== 0) return;
		dragRef.current = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			right: fabRight,
			bottom: fabBottom,
			moved: false,
		};
		event.currentTarget.setPointerCapture?.(event.pointerId);
	};

	const handlePointerMove = (event) => {
		const drag = dragRef.current;
		if (!drag || drag.pointerId !== event.pointerId) return;
		const dx = event.clientX - drag.startX;
		const dy = event.clientY - drag.startY;
		if (!drag.moved) {
			if (Math.abs(dx) <= DRAG_THRESHOLD && Math.abs(dy) <= DRAG_THRESHOLD) {
				return;
			}
			drag.moved = true;
			setIsDragging(true);
		}
		setPosition(
			clampToBounds(
				{ right: drag.right - dx, bottom: drag.bottom - dy },
				readViewport(),
			),
		);
	};

	const endDrag = (event) => {
		const drag = dragRef.current;
		if (!drag || drag.pointerId !== event.pointerId) return;
		dragRef.current = null;
		event.currentTarget.releasePointerCapture?.(event.pointerId);
		if (!drag.moved) return;
		suppressClickRef.current = true;
		setIsDragging(false);
		setPosition((current) =>
			settle(current || { right: fabRight, bottom: fabBottom }, readViewport()),
		);
	};

	useEffect(() => {
		if (!isOpen) return undefined;
		const handleClickOutside = (event) => {
			if (
				panelRef.current &&
				!panelRef.current.contains(event.target) &&
				fabRef.current &&
				!fabRef.current.contains(event.target)
			) {
				setIsOpen(false);
			}
		};
		const handleEsc = (event) => {
			if (event.key === "Escape") setIsOpen(false);
		};
		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("keydown", handleEsc);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", handleEsc);
		};
	}, [isOpen]);

	const toggle = () => {
		if (suppressClickRef.current) {
			suppressClickRef.current = false;
			return;
		}
		if (isOpen) {
			setIsOpen(false);
			return;
		}
		if (isLoading) return;
		if (!authUser) {
			window.location.href = `${APP_URL}/login`;
			return;
		}
		setIsOpen(true);
	};

	// Keep the panel tethered to wherever the fab now sits, without letting it
	// hang off the top or the side of the viewport.
	const panelWidth = Math.min(
		PANEL_WIDTH,
		viewport.width - PANEL_EDGE_MARGIN * 2,
	);
	const panelRightOffset = clamp(
		fabRight,
		PANEL_EDGE_MARGIN,
		Math.max(
			PANEL_EDGE_MARGIN,
			viewport.width - panelWidth - PANEL_EDGE_MARGIN,
		),
	);
	const panelBottomOffset = clamp(
		fabBottom + FAB_SIZE + panelGap,
		PANEL_EDGE_MARGIN,
		Math.max(
			PANEL_EDGE_MARGIN,
			viewport.height - PANEL_MIN_HEIGHT - PANEL_EDGE_MARGIN,
		),
	);

	return (
		<div className="bodhi-float">
			{isOpen && (
				<div ref={panelRef}>
					<Panel
						onClose={() => setIsOpen(false)}
						bottomOffset={panelBottomOffset}
						rightOffset={panelRightOffset}
					/>
				</div>
			)}

			<button
				ref={fabRef}
				type="button"
				className="bodhi-float__fab"
				aria-label={isOpen ? "Close Bodhi chat" : "Chat with Bodhi"}
				aria-expanded={isOpen}
				data-loading={isLoading ? "true" : "false"}
				data-dragging={isDragging ? "true" : "false"}
				onClick={toggle}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={endDrag}
				onPointerCancel={endDrag}
				style={{ right: fabRight, bottom: fabBottom }}
			>
				{isOpen ? (
					<IconClose className="bodhi-float__fab-icon" />
				) : (
					<IconSparkles className="bodhi-float__fab-icon" />
				)}
			</button>
		</div>
	);
};

export default BodhiChatFloating;
